import BaseController from "niteroi/packages/system/controllers/Base.controller";
import Localization from "sap/base/i18n/Localization";
import {ListItemBase$DetailPressEvent} from "sap/m/ListItemBase";
import {Button$PressEvent} from "sap/m/Button";
import Toolbar from "sap/m/Toolbar";
import HeaderContainer from "sap/m/HeaderContainer";
import Page from "sap/m/Page";
import {ILoadDataResponse, IUser} from "niteroi/packages/system/models/interfaces";
import MessageBox from "sap/m/MessageBox";
import UIComponent from "sap/ui/core/UIComponent";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import Theming from "sap/ui/core/Theming";
import {SharedServices} from "niteroi/services/SharedServices";
import Default from "niteroi/packages/system/models/Default";

export default class Users extends BaseController {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    public onInit(): void {
        super.onInit();
        const router = this.getRouter();
        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);

            if (route.getPattern().includes("system/users/add")) {
                this.getModel("user").setData(Default.initUser());
                this.getModel("user").attachPropertyChange(this.setModelEditMode.bind(this));

                const roles = this.createRequestModel();
                roles.loadData("/api/roles", "", true, "GET", false, true, this.getRequestHeaders()).then(() => {
                    this.setModel(roles, "roles");
                });
            } else {
                if (route.getPattern().includes("system/users/edit")) {
                    const roles = this.createRequestModel();
                    roles.loadData("/api/roles", "", true, "GET", false, true, this.getRequestHeaders()).then(() => {
                        this.setModel(roles, "roles");
                    });
                }
                route.detachPatternMatched(this.onPatternMatchedHandler);
                route.attachPatternMatched(this.onPatternMatchedHandler);
            }
        }
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        const params = event.getParameters();
        const id = (params.arguments as { id: string }).id ?? "";
        this.getData(id);
    }

    protected getData(objectId: string = ""): void {
        this.getView().setBusy(true);
        const modelName = objectId ? "user" : "users";
        const model = this.getModel(modelName);
        const url = objectId ? `/api/users/${objectId}` : "/api/users";
        model.loadData(url, "", true, "GET", false, true, this.getRequestHeaders()).then(() => {
            this.originalData = objectId ? (Object.assign({}, model.getData() as IUser)) : Default.initUser();
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            this.getView().setBusy(false);
        });
    }

    public onExit(): void {
        this.isModelSaved = true;
        this.originalData = {};
    }

    public onPressMenuItemAdd(): void {
        this.isModelSaved = true;
        this.originalData = {};
        this.getModel("user").setData(Object.assign({}, Default.initUser(), {isModelSaved: this.isModelSaved}));

        this.navTo("system/users/add", {}, false);
    }

    public onPressColumnListItemDetail(event: ListItemBase$DetailPressEvent): void {
        const source = event.getSource();
        const bindingContext = source.getBindingContext("users");
        const selectedListItem = bindingContext.getModel().getProperty(bindingContext.getPath()) as IUser;
        this.originalData = selectedListItem;
        this.isModelSaved = true;
        selectedListItem.isModelSaved = this.isModelSaved;
        this.navTo("system/users/edit", {"id": selectedListItem.userId}, false);
    }

    public onPressButtonSaveFormEdit(event: Button$PressEvent): void {
        const page = (((event.getSource().getParent() as Toolbar).getParent() as HeaderContainer).getParent() as Page);
        page.setBusy(true);
        const model = this.getModel("user");
        const itemData = model.getData() as IUser;

        if (itemData.newPassword || itemData.repeatNewPassword) {
            if (itemData.newPassword !== itemData.repeatNewPassword) {
                this.showToast(this.getText("msgPasswordsDoNotMatch"));
                page.setBusy(false);
                return;
            }
            itemData.password = itemData.newPassword;
            delete itemData.newPassword;
            delete itemData.repeatNewPassword;
        }

        let isNew = false;
        if (!itemData.userId) {
            isNew = true;
            itemData.companyId = this.getModel("session").getProperty("/company/companyId") as string;
            if (!itemData.password) {
                this.showToast(this.getText("msgPasswordRequired"));
                page.setBusy(false);
                return;
            }
        }

        if (itemData.firstName && itemData.email) {
            const method = isNew ? "POST" : "PUT";
            const headers = this.getRequestHeaders();
            const replacer = (key: string, value: string | number | object) =>
                typeof value === 'undefined' ? null : value;
            const data = JSON.stringify(itemData, replacer);
            const userLanguage = window.localStorage.getItem("userLanguage");
            const sessionModel = this.getModel("session");
            const sessionUserId = sessionModel.getProperty("/user/userId") as string;
            const url = isNew ? "/api/users" : `/api/users/${itemData.userId}`;
            model.loadData(url, data, true, method, false, true, headers).then(() => {
                const sessionModel = this.getModel("session");
                const responseData = model.getData();
                if (sessionUserId === responseData.userId) {
                    const userData = Object.assign({}, {password: "", newPassword: "", repeatNewPassword: ""}, responseData);
                    sessionModel.setProperty("/user", userData);
                    if (responseData.locale) {
                        Localization.setLanguage(responseData.locale);
                        window.localStorage.setItem("userLanguage", responseData.locale);
                    }
                    if (responseData.theme) {
                        window.localStorage.setItem("userTheme", responseData.theme);
                        const themeName = SharedServices.getThemeName(responseData.theme);
                        Theming.setTheme(themeName);
                        this.getModel("theme").setProperty("/theme", themeName);
                    }
                }
                this.navTo("system/users/edit", {id: model.getProperty("/userId")}, false);
                return responseData;
            }).then((responseData: IUser) => {
                if (responseData.userId === sessionUserId && responseData.locale && responseData.locale !== userLanguage) {
                    window.location.reload();
                }
                this.showToast(this.getText("msgUserSavedSuccess"));
            }).catch((response: ILoadDataResponse) => {
                const responseText = JSON.parse(response.responseText);
                this.showToast(`${responseText.code} - ${responseText.error}`);
            }).finally(() => {
                page.setBusy(false);
            })
        } else {
            page.setBusy(false);
        }
    }

    public onPressButtonDiscardFormEdit(): void {
        this.discardChanges("user");
    }

    public onPressButtonDeactivateUserFormEdit(): void {
        (this.getOwnerComponent() as UIComponent).getRootControl().setBusy(true);
        const model = this.getModel("user");
        const message = model.getProperty("/isActive") ? this.getText("toggleUserStatusDeactivate") : this.getText("toggleUserStatusActivate");
        MessageBox.confirm(message, {
            onClose: (action: string) => {
                if (action === MessageBox.Action.OK) {
                    const isActive = model.getProperty("/isActive") ? 0 : 1;
                    const requestModel = this.createRequestModel();
                    const userId = model.getProperty("/userId") as string;
                    requestModel.loadData(`/api/users`, JSON.stringify({
                        userId: userId,
                        isActive: isActive
                    }), true, 'PUT', false, true, this.getRequestHeaders()).then(() => {
                        model.setProperty("/isActive", isActive);
                        this.showToast(this.getText("msgUserStatusUpdatedSuccess"));
                    }).catch((response: ILoadDataResponse) => {
                        const responseText = JSON.parse(response.responseText);
                        this.showToast(`${responseText.code} - ${responseText.error}`);
                    }).finally(() => {
                        (this.getOwnerComponent() as UIComponent).getRootControl().setBusy(false);
                    });
                }
            }
        });
    }

    public onPressButtonDeleteAvatarFormEdit(): void {
        const companyModel = this.getModel("user");
        companyModel.setProperty("/user", "");
    }

    public onSearch(event: SearchField$LiveChangeEvent): void {
        this.onSearchList(event, "usersTable", ["firstName", "lastName", "birthday", "email", "city", "country"]);
    }
}
